import { toCalendarDateString } from '@/utils/dateUtils';

/** Filter values the backend will accept on the analytics endpoints.
 *  Anything outside this set must be coerced to a safe default before the
 *  query fires — a typo'd URL param would otherwise hit the backend and
 *  fall through to the default `last-week` window (or 400). */
const ALLOWED_FILTERS = [
  'last-week',
  'last-month',
  'last-6-months',
  'last-year',
  'all-time',
  'custom',
] as const;
export type AnalyticsFilter = (typeof ALLOWED_FILTERS)[number];

const DEFAULT_FILTER: AnalyticsFilter = 'all-time';

const isAllowedFilter = (s: string): s is AnalyticsFilter =>
  (ALLOWED_FILTERS as readonly string[]).includes(s);

export interface DateFilteredData {
  filter?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}

export interface DateRangeQueryParams {
  /** Preset filter name (`last-week`, `all-time`, etc.) or `'custom'`. */
  filter: AnalyticsFilter;
  /** ISO YYYY-MM-DD start, only set when `filter === 'custom'`. */
  customStartDate: string | undefined;
  /** ISO YYYY-MM-DD end, only set when `filter === 'custom'`. */
  customEndDate: string | undefined;
}

/**
 * Normalizes the date-range state passed down from CalendarFilter into the
 * shape every analytics React Query hook expects.
 *
 * Two analytics hosts (CampaignTabComponent + AnalyticsComponent) used to
 * inline the same derivation, leading to copy-paste drift risk. Centralized
 * here so a future change (e.g., timezone tweak, new preset) lands once.
 *
 * Unknown filter strings (e.g. from a stale URL or a typo) coerce to
 * `'all-time'` so a broken query param can't poison the query cache.
 */
export const useDateRangeQueryParams = (
  dateFilteredData: DateFilteredData | undefined,
): DateRangeQueryParams => {
  const raw = dateFilteredData?.filter ?? '';
  const filter: AnalyticsFilter = isAllowedFilter(raw) ? raw : DEFAULT_FILTER;
  const isCustom = filter === 'custom';
  return {
    filter,
    customStartDate:
      isCustom && dateFilteredData?.startDate instanceof Date
        ? toCalendarDateString(dateFilteredData.startDate)
        : undefined,
    customEndDate:
      isCustom && dateFilteredData?.endDate instanceof Date
        ? toCalendarDateString(dateFilteredData.endDate)
        : undefined,
  };
};
